import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, User, Calendar, Car, MessageSquare, Sparkles } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const NOTIF_ICONS = {
  booking_new: { icon: Car, color: 'text-green-400', bg: 'bg-green-400/10' },
  booking_confirmed: { icon: Check, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  ride_offer: { icon: Car, color: 'text-purple-400', bg: 'bg-purple-400/10' },
  ride_available: { icon: Car, color: 'text-orange-400', bg: 'bg-orange-400/10' },
  event_new: { icon: Calendar, color: 'text-pink-400', bg: 'bg-pink-400/10' },
  corner_new: { icon: MessageSquare, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
  system: { icon: Sparkles, color: 'text-amber-400', bg: 'bg-amber-400/10' },
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/notifications?limit=20`, { headers });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silent
    }
  }, [token]);

  // Poll every 30s + fetch on mount
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/api/notifications/mark-read`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  };

  const handleNotifClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        await axios.post(`${API}/api/notifications/${notif.notification_id}/read`, {}, { headers });
        setNotifications(prev =>
          prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {
        // silent
      }
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const deleteNotif = async (e, notifId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/notifications/${notifId}`, { headers });
      setNotifications(prev => prev.filter(n => n.notification_id !== notifId));
      setUnreadCount(prev => {
        const wasUnread = notifications.find(n => n.notification_id === notifId && !n.read);
        return wasUnread ? Math.max(0, prev - 1) : prev;
      });
    } catch {
      // silent
    }
  };

  const timeAgo = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        className="relative p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-[18px] h-[18px]" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1 leading-none"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="fixed right-3 top-[60px] md:absolute md:right-0 md:top-full md:mt-2 w-[min(340px,calc(100vw-24px))] max-h-[420px] rounded-xl border border-border/50 bg-card backdrop-blur-xl shadow-2xl overflow-hidden z-[100]"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
            <h3 className="text-sm font-bold">Activity</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] text-primary font-medium hover:underline"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="overflow-y-auto max-h-[360px]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Bell className="w-8 h-8 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Activity will show up here</p>
              </div>
            ) : (
              notifications.map(notif => {
                const cfg = NOTIF_ICONS[notif.type] || NOTIF_ICONS.system;
                const Icon = cfg.icon;
                return (
                  <div
                    key={notif.notification_id}
                    onClick={() => handleNotifClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-muted/40 transition-colors border-b border-border/10 last:border-0 ${!notif.read ? 'bg-primary/5' : ''}`}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    {/* Actor avatar or type icon */}
                    {notif.actor_photo ? (
                      <Avatar className="h-9 w-9 flex-shrink-0 mt-0.5">
                        <AvatarImage src={notif.actor_photo} />
                        <AvatarFallback className="text-xs">{(notif.actor_name || '?')[0]}</AvatarFallback>
                      </Avatar>
                    ) : (
                      <div className={`flex-shrink-0 w-9 h-9 rounded-full ${cfg.bg} flex items-center justify-center mt-0.5`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!notif.read ? 'font-medium' : 'text-muted-foreground'}`}>
                        {notif.message}
                      </p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      )}
                      <button
                        onClick={(e) => deleteNotif(e, notif.notification_id)}
                        className="p-1 rounded-full text-muted-foreground/40 hover:text-red-400 hover:bg-red-400/10 transition-colors opacity-0 group-hover:opacity-100"
                        style={{ opacity: 1 }}
                        data-testid={`delete-notif-${notif.notification_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;
